import { useEffect, useLayoutEffect, useRef } from 'react';

const NEAR_TOP_PX = 80;
const NEAR_BOTTOM_PX = 48;

function distanceFromBottom(list) {
  return list.scrollHeight - list.scrollTop - list.clientHeight;
}

/**
 * Держит ленту у нижнего края, пока пользователь сам не прокрутил вверх.
 * Подгрузка старых сообщений не сдвигает текущую позицию.
 * Программный скролл не считается жестом пользователя и не грузит историю.
 */
export function useChatListScroll({ messages, loading, onLoadOlder }) {
  const listRef = useRef(null);
  const contentRef = useRef(null);
  const stickToBottomRef = useRef(true);
  const snapshotRef = useRef(null);
  const ignoreScrollRef = useRef(false);

  const runWithoutScrollHandler = (action) => {
    ignoreScrollRef.current = true;
    action();
    requestAnimationFrame(() => {
      ignoreScrollRef.current = false;
    });
  };

  const pinToBottom = () => {
    const list = listRef.current;
    if (!list) return;
    runWithoutScrollHandler(() => {
      list.scrollTop = list.scrollHeight;
    });
    stickToBottomRef.current = true;
  };

  useLayoutEffect(() => {
    const list = listRef.current;
    if (!list || loading) return;

    const firstId = messages[0]?.id;
    const previous = snapshotRef.current;
    const olderPrepended = previous != null && firstId !== previous.firstId && messages.length > previous.count;
    const ownPendingMessage = messages[messages.length - 1]?.pending;

    if (olderPrepended && !ownPendingMessage) {
      const heightDelta = list.scrollHeight - previous.scrollHeight;
      runWithoutScrollHandler(() => {
        list.scrollTop = previous.scrollTop + heightDelta;
      });
    } else if (ownPendingMessage || stickToBottomRef.current || previous == null) {
      pinToBottom();
    }

    snapshotRef.current = {
      firstId,
      count: messages.length,
      scrollHeight: list.scrollHeight,
      scrollTop: list.scrollTop,
    };
  }, [messages, loading]);

  useEffect(() => {
    const content = contentRef.current;
    if (!content) return undefined;

    const observer = new ResizeObserver(() => {
      const list = listRef.current;
      if (!list) return;
      if (stickToBottomRef.current) pinToBottom();
      if (snapshotRef.current) {
        snapshotRef.current.scrollHeight = list.scrollHeight;
        snapshotRef.current.scrollTop = list.scrollTop;
      }
    });
    observer.observe(content);

    return () => observer.disconnect();
  }, [loading, messages.length]);

  const onScroll = () => {
    const list = listRef.current;
    if (!list || ignoreScrollRef.current) return;

    stickToBottomRef.current = distanceFromBottom(list) < NEAR_BOTTOM_PX;

    const overflows = list.scrollHeight > list.clientHeight + 8;
    if (overflows && list.scrollTop <= NEAR_TOP_PX) onLoadOlder?.();
  };

  return { listRef, contentRef, onScroll };
}
